import { XMarkIcon } from "@heroicons/react/24/outline";
import React from "react";

export function formatDateOnly(dateStr: string): string {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export interface RebootLiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  /** When false, backdrop click and close button are disabled (e.g. during loading) */
  canClose?: boolean;
  maxWidth?: "sm" | "md" | "lg";
  dataTestId?: string;
  children: React.ReactNode;
}

const maxWidthClasses = {
  sm: "max-w-[420px]",
  md: "max-w-[480px]",
  lg: "max-w-[520px]",
};

export const RebootLiveModal: React.FC<RebootLiveModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  canClose = true,
  maxWidth = "md",
  dataTestId,
  children,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-[2px] flex items-center justify-center z-[999999]"
      data-testid={dataTestId}
      onClick={(e) => {
        if (e.target === e.currentTarget && canClose) onClose();
      }}
    >
      <div
        className={`bg-[#1d1d1f] border border-[rgba(255,255,255,0.1)] rounded-xl w-full ${maxWidthClasses[maxWidth]} overflow-hidden shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.5)]`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-lg bg-orange-500/15 flex items-center justify-center">
              {icon}
            </div>
            <div>
              <h2 className="text-white text-lg font-semibold">{title}</h2>
              {subtitle && (
                <p className="text-[#7e7e81] text-xs mt-0.5">{subtitle}</p>
              )}
            </div>
          </div>
          {canClose && (
            <button
              onClick={onClose}
              data-testid="reboot-live-modal-close-button"
              className="size-8 rounded-md flex items-center justify-center hover:bg-[#252527] transition-colors"
            >
              <XMarkIcon className="size-4 text-[#7e7e81]" />
            </button>
          )}
        </div>

        {children}
      </div>
    </div>
  );
};

/** Stat card for session metadata (Player, Movement, Session Date) */
export function StatCard({
  icon,
  value,
  label,
  truncate,
  dataTestId,
}: {
  icon: React.ReactNode;
  value: string;
  label: string;
  truncate?: boolean;
  dataTestId?: string;
}) {
  return (
    <div
      className="flex flex-col gap-1.5 items-center text-center px-2.5 py-2.5 rounded-lg bg-[#111113] border border-[rgba(255,255,255,0.04)]"
      data-testid={dataTestId}
    >
      <div className="text-orange-500">{icon}</div>
      <div className="w-full min-w-0">
        <p
          className={`text-white text-xs font-semibold leading-tight ${truncate ? "truncate" : ""}`}
        >
          {value}
        </p>
        <p className="text-[#555] text-[10px] mt-0.5">{label}</p>
      </div>
    </div>
  );
}
